import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, Grid, Paper, Typography, useTheme } from '@mui/material';
import TopBar from '../../../components/TopBar';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, map } from 'lodash';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomTextInput from '../../../components/CustomTextInput';
import CountrySelect from '../../../components/form/CountrySelect';
import UAESubdivisionSelect from '../../../components/form/UAESubdivisionSelect';
import RegistrationTypeSelect from '../../../components/form/RegistrationTypeSelect';
import PartyIdentificationSchemeSelect from '../../../components/form/PartyIdentificationSchemeSelect';
import { CustomerValidation } from '../../../validations/CustomerValidation';
import { fetchParticipantRequest } from '../../../api/participants/participantsSlice';
import {
  getCountryOptionByCode,
  getRegistrationTypeOptionByCode,
  getSubdivisionOptionByCode,
  getIso6523IcdOptionByCode,
} from '../../../api/codelist/codeListsSlice';
import TaxSchemeSelect from '../../../components/form/TaxSchemeSelect';
import { DEFAULT_CUSTOMER_TYPE, UAE_COUNTRY_CODE } from '../../../constants/defaults';
import { normalizePartyTaxSchemeForSubmit } from '../../../utils/partyTaxSchemeSubmit';
import { mapPartyIdentificationForSubmit } from '../../../utils/partyIdentificationSubmit';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import TrnVatField from '../../common/components/TrnVatField';

export default function CustomerForm({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  edit = false,
  readOnly = false,
  customerType = DEFAULT_CUSTOMER_TYPE,
  isLoading = false,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isIndividual = customerType === 'INDIVIDUAL';
  const { customers: customerTranslations, validations: validationTranslations, language } = useTranslatedMessages();
  const page = customerTranslations?.pages?.form ?? {};
  const actions = customerTranslations?.actions ?? {};
  const labels = customerTranslations?.form?.labels ?? {};

  const countryOption = useSelector(getCountryOptionByCode(initialData.postal_address?.country_code));
  const subdivisionOption = useSelector(getSubdivisionOptionByCode(initialData.postal_address?.country_subentity));
  const schemeAgencyIdOption = useSelector(getRegistrationTypeOptionByCode(initialData.scheme_agency_id));
  const partyIdentificationSchemeOption = useSelector(
    getIso6523IcdOptionByCode(initialData.party_identification_scheme_id),
  );
  const registrationTypeOption = useSelector(getRegistrationTypeOptionByCode(initialData.registration_type));
  const passportIssuingCountryOption = useSelector(getCountryOptionByCode(initialData.passport_issuing_country_code));
  const subscription = useSelector((state) => state.subscription.subscription);

  const participant = useSelector((state) => state.participants.participant);

  const [participantId, setParticipantId] = useState(initialData.participant_id || '');
  const [peppolParticipant, setPeppolParticipant] = useState(initialData.peppol_participant || {});
  const [loading, setLoading] = useState(false);
  const [loadingParticipant, setLoadingParticipant] = useState(false);
  const isTrnVerificationEnabled = subscription?.features?.enable_trn_verification;

  const {
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(CustomerValidation(isIndividual, validationTranslations?.customer)),
    defaultValues: {
      ...initialData,
      customer_type: customerType,
      postal_address: {
        ...initialData.postal_address,
        street_name: initialData.postal_address?.street_name,
        additional_street_name: initialData.postal_address?.additional_street_name,
        address_line: initialData.postal_address?.address_line,
        city_name: initialData.postal_address?.city_name,
        postal_zone: initialData.postal_address?.postal_zone,
        country_code: countryOption,
        country_subentity:
          initialData.postal_address?.country_code == UAE_COUNTRY_CODE
            ? (subdivisionOption ?? null)
            : initialData.postal_address?.country_subentity,
      },
      telephone: initialData.telephone,
      scheme_agency_id: schemeAgencyIdOption,
      party_identification_id: initialData.party_identification_id ?? '',
      party_identification_scheme_id: partyIdentificationSchemeOption ?? null,
      registration_type: registrationTypeOption,
      passport_issuing_country_code: passportIssuingCountryOption,
      peppol_participant: initialData.peppol_participant ? initialData.peppol_participant : {},
      participant_id:
        initialData?.endpoint_id && initialData?.endpoint_scheme_id
          ? `${initialData?.endpoint_scheme_id}:${initialData?.endpoint_id}`
          : '',
      party_tax_scheme: {
        tax_scheme: initialData.party_tax_scheme?.tax_scheme ?? '',
        company_id: initialData.party_tax_scheme?.company_id ?? '',
      },
    },
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, apiErrors]);

  useEffect(() => {
    const watchParticipantId = watch((value, { name }) => {
      if (name === 'participant_id') {
        setParticipantId(value.participant_id);
        setLoadingParticipant(true);
      }
    });
    return () => watchParticipantId.unsubscribe();
  }, [watch]);

  useEffect(() => {
    setParticipantId(getValues('participant_id'));
    if (participantId) {
      handleFetchParticipants();
    }
  }, [participantId]);

  const handleFetchParticipants = async () => {
    if (!participantId) return;
    if (!subscription?.features?.enable_peppol) {
      setLoading(false);
      setLoadingParticipant(false);
      return;
    }
    setLoading(true);
    setLoadingParticipant(true);

    const [identifier_code, rest] = participantId.split(':');
    const match = rest ? rest.match(/(\d{2})(.*)/) : [];
    const [special_identifier, identifier] = match ? match.slice(1) : [];

    try {
      if (subscription?.features?.enable_peppol)
        await dispatch(
          fetchParticipantRequest({
            identifier_code,
            identifier,
            special_identifier,
          }),
        );
    } catch (error) {
      console.error('Error fetching participant:', error);
    } finally {
      setLoading(false);
    }
  };

  const isPeppolEmpty = !peppolParticipant?.endpoint_id;

  const handleCountryChange = (value) => {
    const isUAE = value?.code === UAE_COUNTRY_CODE;
    setValue('postal_address.country_subentity', isUAE ? null : '');
    setValue('scheme_agency_id', null);
    setValue('company_id', '');
    setValue('authority_name', null);
    setValue('passport_issuing_country_code', null);
    if (!isIndividual) {
      if (!isUAE) {
        setValue('party_tax_scheme.tax_scheme', '');
        setValue('party_tax_scheme.company_id', '');
        setValue('tin', '');
      }
    }
  };

  const handleRegisterType = () => {
    setValue('company_id', '');
    setValue('authority_name', null);
    setValue('passport_issuing_country_code', null);
  };

  const handleTaxSchemeChange = () => {
    trigger(['party_tax_scheme.company_id', 'party_tax_scheme.tax_scheme']);
  };

  const handleFormSubmit = (data) => {
    const isUAE = data.postal_address?.country_code?.code === UAE_COUNTRY_CODE;
    const submitData = {
      ...data,
      customer_type: customerType,
      endpoint_scheme_id: data.participant_id?.split(':')[0],
      endpoint_id: data.participant_id?.split(':')[1],
      scheme_agency_id: data.scheme_agency_id?.code,
      tin: data.tin?.trim() || data.tin,
      postal_address: {
        ...data.postal_address,
        country_code: data.postal_address?.country_code?.code,
        country: data.postal_address?.country_code?.label,
        country_subentity:
          data.postal_address?.country_code?.code === UAE_COUNTRY_CODE
            ? data.postal_address?.country_subentity?.code
            : data.postal_address?.country_subentity,
      },
      passport_issuing_country_code: data.passport_issuing_country_code?.code,
      ...mapPartyIdentificationForSubmit(data),
    };
    if (!isUAE) {
      delete submitData.party_tax_scheme;
      delete submitData.tin;
    } else {
      const normalized = normalizePartyTaxSchemeForSubmit(submitData.party_tax_scheme);
      if (normalized) {
        submitData.party_tax_scheme = normalized;
      } else {
        delete submitData.party_tax_scheme;
      }
    }

    onSubmit(submitData);
  };

  useEffect(() => {
    setLoadingParticipant(false);
    if (!isEmpty(participant)) {
      const match = participant.idValue?.match(/(\d{2})(.*)/);
      const [special_identifier, identifier] = match ? match.slice(1) : [];

      setPeppolParticipant({
        scheme: participant?.idScheme,
        identifier: identifier,
        identifier_code: participant?.idType,
        special_identifier: special_identifier,
        endpoint_id: participant?.endpoint[0]?.url,
      });
    }
    if (participantId === '') {
      setPeppolParticipant({});
    }
  }, [participant]);

  const registrationType = useWatch({ control, name: 'scheme_agency_id' });
  const countryCode = useWatch({ control, name: 'postal_address.country_code' })?.code;
  const registrationNumberLabel = registrationType
    ? registrationType.code
      ? `${registrationType.label} (${registrationType.code})`
      : registrationType.label
    : (labels.registration_number ?? 'Registration Number');

  return (
    <Paper elevation={0} sx={{ p: '1rem' }}>
      {!readOnly && (
        <Box sx={{ pt: 2, pb: 4 }}>
          <Grid
            container
            sx={{
              display: 'block',
            }}
          >
            <Grid xs={12}>
              <TopBar title={page?.top_bar_title ?? 'Customer Form'} identifier={initialData?.profile_id}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(1) }}>
                  <Button
                    data-testid="customer-profile-cancel-button"
                    key={'cancel'}
                    variant="outlined"
                    onClick={onCancel}
                    disabled={isLoading}
                  >
                    {actions.cancel ?? 'Cancel'}
                  </Button>
                  <Button
                    data-testid="customer-profile-save-button"
                    key={'save'}
                    variant="contained"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isLoading}
                  >
                    {actions.save ?? 'Save'}
                  </Button>
                </Box>
              </TopBar>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="name-input"
              name="name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.name ?? 'Name'}
            />
          </Grid>
          {!isIndividual && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomTextInput
                  data-testid="business-name-input"
                  name="party_name"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={labels.business_name ?? 'Business Name'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomTextInput
                  data-testid="business-name-in-local-language-input"
                  name="party_name_in_local_language"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={labels.business_name_local ?? 'Business Name in Local Language'}
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="email-input"
              name="email"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.email ?? 'Email'}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
          {labels.address ?? 'Address'}
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="street_name-input"
              name="postal_address.street_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.street_name ?? 'Street Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="additional_street_name-input"
              name="postal_address.additional_street_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.additional_street_name ?? 'Additional Street Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="city_name-input"
              name="postal_address.city_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.city_name ?? 'City Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              name="postal_address.address_line"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.address_line ?? 'Address Line'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountrySelect
              data-testid="postal_address.country_code-select"
              name="postal_address.country_code"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.country ?? 'Country'}
              onChange={handleCountryChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {useWatch({ control, name: 'postal_address.country_code' })?.code === UAE_COUNTRY_CODE ? (
              <UAESubdivisionSelect
                errors={errors}
                control={control}
                data-testid="uae-subentity-select"
                name="postal_address.country_subentity"
                readOnly={readOnly}
                label={labels.uae_subentity ?? 'UAE Subentity'}
              />
            ) : (
              <CustomTextInput
                data-testid="country-subentity-input"
                name="postal_address.country_subentity"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={labels.country_subentity ?? 'Country Subentity'}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="postal_zone-input"
              name="postal_address.postal_zone"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.postal_zone ?? 'Postal Zone'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-tetsid="telephone-input"
              name="telephone"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.telephone ?? 'Telephone'}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
          {labels.business_identifiers ?? 'Business Identifiers'}
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          {!isIndividual && countryCode === UAE_COUNTRY_CODE && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TaxSchemeSelect
                  data-testid="tax_scheme-select"
                  name="party_tax_scheme.tax_scheme"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={labels.tax_scheme ?? 'Tax Scheme'}
                  onTaxSchemeChange={handleTaxSchemeChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {isTrnVerificationEnabled ? (
                  <TrnVatField
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    watch={watch}
                    readOnly={readOnly}
                    label={labels.vat ?? 'VAT'}
                    labels={labels.trn_verify ?? {}}
                  />
                ) : (
                  <CustomTextInput
                    data-testid="tax-identifier-input"
                    name="party_tax_scheme.company_id"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={labels.vat ?? 'VAT'}
                    inputProps={{ maxLength: 15 }}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CustomTextInput
                  data-testid="tin-input"
                  name="tin"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={labels.tin ?? 'TIN'}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <RegistrationTypeSelect
              data-testid="registration-type-select"
              errors={errors}
              readOnly={readOnly}
              control={control}
              name="scheme_agency_id"
              customerType={isIndividual ? customerType : undefined}
              onChange={handleRegisterType}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="registration-number-input"
              name="company_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={registrationNumberLabel}
            />
          </Grid>
          {registrationType?.code === 'TL' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="registration_authority-input"
                name="authority_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={labels.registration_authority ?? 'Registration Authority'}
              />
            </Grid>
          )}
          {registrationType?.code === 'PAS' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CountrySelect
                data-testid="passport_issuing_country_code-select"
                name="passport_issuing_country_code"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={labels.passport_issuing_country ?? 'Passport Issuing Country'}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <PartyIdentificationSchemeSelect
              data-testid="party_identification_scheme_id-select"
              name="party_identification_scheme_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.party_identification_scheme_id ?? 'Party Identification Scheme'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="party_identification_id-input"
              name="party_identification_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={labels.party_identification_id ?? 'Party Identification'}
            />
          </Grid>
          {!isIndividual && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                name="participant_id"
                control={control}
                errors={errors}
                label={labels.participant_id ?? 'Participant ID'}
                placeholder="0235:234234234"
                readOnly={readOnly}
              />
            </Grid>
          )}
        </Grid>

        {/*{(edit || readOnly) && (*/}
        {/*  <PeppolDetails*/}
        {/*    control={control}*/}
        {/*    errors={errors}*/}
        {/*    getValues={getValues}*/}
        {/*    setParticipantId={setParticipantId}*/}
        {/*    handleFetchParticipants={handleFetchParticipants}*/}
        {/*    loading={loading}*/}
        {/*    readOnly={readOnly}*/}
        {/*    loadingParticipant={loadingParticipant}*/}
        {/*    isPeppolEmpty={isPeppolEmpty}*/}
        {/*    peppolParticipant={peppolParticipant}*/}
        {/*    peppolLabels={labels}*/}
        {/*    peppolParticipantDescription={*/}
        {/*      labels.peppol_description ??*/}
        {/*      'Fetch Peppol participant ID to send E-Documents directly over Peppol network.'*/}
        {/*    }*/}
        {/*  />*/}
        {/*)}*/}
      </Box>
    </Paper>
  );
}
